/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: prop-types library.
import _ from "underscore"; // Npm: underscore.js library.
import { DataGrid, GridFooter } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { HiArrowPath, HiArrowUturnDown } from "react-icons/hi2"; // Npm: React icons.
import { Button, Flex, Text, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import TableSpinner from "components/TableSpinner";
import CustomerAccountSelector from "components/CustomerAccountSelectorPerformance";

/*
 * GRAPHS
 */
import CustomerAccountMarginReportQuery from "./__query__/index.customerAccount.marginReport.query";

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [selectedCustomerAccount, setSelectedCustomerAccount] =
    React.useState(void 0);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [allCustomerAccountIds, setAllCustomerAccountIds] = React.useState([]);
  const _QueryCustomerAccountMarginReport = useQuery(
    CustomerAccountMarginReportQuery,
    {
      variables: {
        customerAccountId: selectedCustomerAccount,
        customerAccountIds: selectedCustomerAccount
          ? void 0
          : allCustomerAccountIds,
        startDate: dateRange.selection.startDate,
        endDate: dateRange.selection.endDate,
      },
      fetchPolicy: Object.React.App.fetchPolicy,
      skip:
        !selectedCustomerAccount &&
        (!allCustomerAccountIds || allCustomerAccountIds?.length === 0),
    },
  );
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Data assignment.
  const _data =
    _QueryCustomerAccountMarginReport?.data?.CustomerAccountMarginReport
      ?._reportData ?? [];

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryCustomerAccountMarginReport.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryCustomerAccountMarginReport.refetch]);

  // Total row values
  const _totalRow = React.useMemo(() => {
    const _totalsDetail =
      _QueryCustomerAccountMarginReport?.data?.CustomerAccountMarginReport
        ?.totalsDetail;

    return {
      revenue: parseFloat(_totalsDetail?.totalRevenue ?? 0).toFixed(2),
      cost: parseFloat(_totalsDetail?.totalCost ?? 0).toFixed(2),
      margin: parseFloat(_totalsDetail?.totalMargin ?? 0).toFixed(2),
    };
  }, [_QueryCustomerAccountMarginReport?.data]);

  // Columns definition.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.NO",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "countryName",
        headerName: "Company Name",
        description: "Name of the company.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "totalSms",
        headerName: "Total SMS",
        description: "Total SMS count.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "revenue",
        headerName: "Revenue (in %)",
        description: "Revenue percentage.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "cost",
        headerName: "Cost (in %)",
        description: "Cost percentage.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "margin",
        headerName: "Margin (in %)",
        description: "Margin percentage.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
      },
    ],
    [],
  );

  // Rows definition.
  const rows = React.useMemo(
    () =>
      _.compact(
        _data?.map((item, index) => {
          const rowId = item?.id ?? item?.customeraccountid ?? `customer-account-margin-${paginationModel.page}-${paginationModel.pageSize}-${index}`

          return rowId ? {
            id: rowId,
            sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
            countryName: item?.displayName ?? "-",
            totalSms: item?.totalsmscount ?? "-",
            revenue: parseFloat(item?.revenue ?? 0)?.toFixed?.(2) ?? "-",
            cost: parseFloat(item?.cost ?? 0)?.toFixed?.(2) ?? "-",
            margin: parseFloat(item?.margin ?? 0)?.toFixed?.(2) ?? "-",
          } : null
        }),
      ),
    [_data, paginationModel.page, paginationModel.pageSize],
  );

  // Return component.
  return (
    <>
      <Flex className="reportsCustomer base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="99vh"
          maxH="99vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justifyContent={
              _isCurrentViewMobile ? "flex-start" : "space-between"
            }
            alignItems={_isCurrentViewMobile ? "flex-start" : "center"}
            gap="12px"
            flexWrap="wrap"
          >
            <Flex
              flex={_isCurrentViewMobile ? "unset" : 0.6}
              gap="12px"
              flexDir="column"
              w={_isCurrentViewMobile ? "100%" : "auto"}
            >
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date"}
                .
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              alignItems="end"
              gap={_isCurrentViewMobile ? "8px" : "12px"}
              flexWrap={_isCurrentViewMobile ? "nowrap" : "wrap"}
              w={_isCurrentViewMobile ? "100%" : "auto"}
            >
              <Flex
                flexDir="column"
                gap={_isCurrentViewMobile ? "8px" : "12px"}
                w={_isCurrentViewMobile ? "100%" : "240px"}
                justify="end"
                alignItems={_isCurrentViewMobile ? "flex-start" : "end"}
              >
                <Text
                  as="h3"
                  fontSize={17}
                  fontWeight={500}
                  display="flex"
                  alignItems="center"
                  color="brand.500"
                  gap="6px"
                >
                  {_isCurrentViewMobile
                    ? "Filter Accounts"
                    : "Select customer account"}
                  .
                  <Text transform="scale(-1, 1)">
                    <HiArrowUturnDown />
                  </Text>
                </Text>
                <CustomerAccountSelector
                  type="retail"
                  accountId={passOn?.accountId}
                  onLoad={setAllCustomerAccountIds}
                  onChange={(e) =>
                    setSelectedCustomerAccount(
                      e.target.value?.includes("(") &&
                        e.target.value?.includes(")")
                        ? e.target.value?.split("(")[1]?.split(")")[0]
                        : void 0,
                    )
                  }
                />
              </Flex>
              <Button
                borderRadius="8px"
                fontWeight="400"
                minW="20px"
                flexShrink={0}
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            paginationMode="client"
            disableColumnFilter
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  title="Kindly Choose Customer Account to see performance."
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  title="Kindly Choose Customer Account to see performance."
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              footer: () => (
                <>
                  {_isCurrentViewMobile ? (
                    <Flex
                      w="100%"
                      border="1px solid"
                      borderColor="#D8E3FC"
                      px="12px"
                      py="10px"
                      gap="14px"
                      flexWrap="nowrap"
                      justify="flex-start"
                      align="center"
                      overflowX="auto"
                    >
                      <Text
                        fontWeight={700}
                        color="brand.500"
                        fontSize="13px"
                        whiteSpace="nowrap"
                      >
                        Total
                      </Text>
                      <Text
                        fontWeight={600}
                        fontSize="13px"
                        whiteSpace="nowrap"
                      >
                        <b>Revenue:</b> {_totalRow.revenue}
                      </Text>
                      <Text
                        fontWeight={600}
                        fontSize="13px"
                        whiteSpace="nowrap"
                      >
                        <b>Cost:</b> {_totalRow.cost}
                      </Text>
                      <Text
                        fontWeight={600}
                        fontSize="13px"
                        whiteSpace="nowrap"
                      >
                        <b>Margin:</b> {_totalRow.margin}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex
                      w="100%"
                      // borderTop='1px solid'
                      borderBottom="1px solid"
                      borderLeft="1px solid"
                      borderRight="1px solid"
                      borderColor="#D8E3FC"
                      bg="white"
                      px="16px"
                      height="58px"
                      alignItems="center"
                    >
                      <Flex w="80px" justify="center">
                        <Text
                          fontWeight={700}
                          color="brand.500"
                          fontSize="14px"
                        ></Text>
                      </Flex>
                      <Flex flex={1} justify="center">
                        <Text
                          fontWeight={700}
                          color="brand.500"
                          fontSize="14px"
                        >
                          Total
                        </Text>
                      </Flex>
                      <Flex flex={1} justify="center">
                        <Text
                          fontWeight={700}
                          color="brand.500"
                          fontSize="14px"
                        ></Text>
                      </Flex>
                      <Flex flex={1} justify="center">
                        <Text fontWeight={600} fontSize="14px">
                          {_totalRow.revenue}
                        </Text>
                      </Flex>
                      <Flex flex={1} justify="center">
                        <Text fontWeight={600} fontSize="14px">
                          {_totalRow.cost}
                        </Text>
                      </Flex>
                      <Flex flex={1} justify="center">
                        <Text fontWeight={600} fontSize="14px">
                          {_totalRow.margin}
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                  <GridFooter />
                </>
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryCustomerAccountMarginReport?.loading}
          />
        </Flex>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index);
